import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { PageBackground } from "../../components/PageBackground";
import {
  MAGIC_LINK_NETWORK_ERROR_MESSAGE,
  readMagicLinkPending,
  readTokenHashParams,
} from "../../utils/authHelpers";

// Landing page for the emailed magic link. Email security scanners prefetch the
// link but never click, so the one-time token is only spent by the button here,
// or automatically when this browser holds the pending marker set at request time.
export function ConfirmSignInScreen() {
  const { verifyMagicLink, isConfigured } = useAuth();
  // Read once: the email the requesting browser bound to its pending marker.
  const [pendingEmail] = useState(() => (isConfigured ? readMagicLinkPending()?.email ?? null : null));
  const [submitting, setSubmitting] = useState(false);
  const [autoVerifying, setAutoVerifying] = useState(() => pendingEmail != null);
  const [networkError, setNetworkError] = useState(false);
  const autoStarted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const verify = useCallback(async (expectedEmail) => {
    const params = readTokenHashParams();
    if (!params) {
      navigate("/login", { replace: true });
      return;
    }
    setNetworkError(false);
    setSubmitting(true);
    const result = await verifyMagicLink(expectedEmail ? { ...params, expectedEmail } : params);
    if (result.retryable) {
      // The request never reached GoTrue, so the token is unspent: keep the params and let the user retry.
      setNetworkError(true);
      setSubmitting(false);
      setAutoVerifying(false);
      return;
    }
    if (result.error) {
      navigate("/login", { replace: true });
      return;
    }
    // Success: drop the token from the router's URL; onAuthStateChange supplies the session.
    const next = new URLSearchParams(location.search);
    next.delete("token_hash");
    next.delete("type");
    const search = next.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
  }, [navigate, location.pathname, location.search, verifyMagicLink]);

  useEffect(() => {
    // Ref guard: StrictMode double-invokes effects and a second verifyOtp on a spent token fails.
    if (!autoVerifying || autoStarted.current) return;
    autoStarted.current = true;
    verify(pendingEmail);
  }, [autoVerifying, pendingEmail, verify]);

  return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", fontFamily:"'DM Sans','Helvetica Neue',sans-serif", color:"#151515", display:"flex", flexDirection:"column", position:"relative", overflow:"hidden" }}>
      <PageBackground />
      <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:24, minHeight:0, position:"relative", zIndex:1 }}>
        <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:20, width:"100%", maxWidth:400, padding:40, boxShadow:"0 3px 15px rgba(0,0,0,0.08)" }}>
          <h1 style={{ fontSize:28, fontWeight:600, color:"#151515", marginBottom:8, textAlign:"center", fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Sign in</h1>
          {autoVerifying ? (
            <p role="status" style={{ color:"#626262", fontSize:14, textAlign:"center", margin:0 }}>Signing you in…</p>
          ) : (
            <>
              <p style={{ color:"#626262", fontSize:14, marginBottom:28, textAlign:"center" }}>Click below to finish signing in to Audiohook.</p>
              {networkError && (
                <p role="alert" style={{ color:"#e53935", fontSize:13, marginBottom:16, textAlign:"center" }}>{MAGIC_LINK_NETWORK_ERROR_MESSAGE}</p>
              )}
              <button
                type="button"
                onClick={() => verify()}
                disabled={submitting || !isConfigured}
                style={{ width:"100%", padding:"13px 0", background: isConfigured ? "#151515" : "#E2E2E2", color: isConfigured ? "#fff" : "#B8B8B8", border:"none", borderRadius:10, fontSize:15, fontWeight:600, cursor: submitting || !isConfigured ? "not-allowed" : "pointer" }}
              >
                {submitting ? "Signing in…" : "Sign in to Audiohook"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
